
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Scale, User, Sparkles, ShieldCheck, RefreshCw } from 'lucide-react';
import { Message } from '../types';
import { ChatService } from '../services/ChatService';

interface SmartConsultantProps {
  suggestions?: string[];
  placeholders?: string[];
  title?: string;
  subtitle?: string;
  context?: string; // Der Kontext bestimmt die System-Instruction (law, medicine, etc.)
  initialMessage?: string;
}

export const SmartConsultant: React.FC<SmartConsultantProps> = ({
  suggestions,
  placeholders,
  title,
  subtitle,
  context = "general",
  initialMessage
}) => {
  const { t } = useTranslation();

  // Use localized defaults if props are not provided
  const processedSuggestions = suggestions || (t('shared.smart_consultant.defaults.suggestions', { returnObjects: true }) as string[]);
  const processedPlaceholders = placeholders || (t('shared.smart_consultant.defaults.placeholders', { returnObjects: true }) as string[]);
  const processedTitle = title || t('shared.smart_consultant.defaults.title');
  const processedSubtitle = subtitle || t('shared.smart_consultant.defaults.subtitle');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'init',
      role: 'bot',
      text: initialMessage || t('chat.welcome'),
      timestamp: Date.now()
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [streamedResponse, setStreamedResponse] = useState('');

  const [placeholder, setPlaceholder] = useState('');
  const [phIndex, setPhIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (input) return;
    const currentText = processedPlaceholders[phIndex];
    if (!currentText) return;

    const typeSpeed = isDeleting ? 30 : 60;
    if (isPaused) {
      const timer = setTimeout(() => { setIsPaused(false); setIsDeleting(true); }, 2000);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (charIndex < currentText.length) {
          setPlaceholder(currentText.substring(0, charIndex + 1));
          setCharIndex(prev => prev + 1);
        } else { setIsPaused(true); }
      } else {
        if (charIndex > 0) {
          setPlaceholder(currentText.substring(0, charIndex - 1));
          setCharIndex(prev => prev - 1);
        } else {
          setIsDeleting(false);
          setPhIndex(prev => (prev + 1) % processedPlaceholders.length);
        }
      }
    }, typeSpeed);
    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, isPaused, phIndex, input, processedPlaceholders]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamedResponse, isTyping]);

  const handleSend = async (text: string) => {
    if (!text.trim() || isTyping) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      text: text,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    setStreamedResponse('');

    // Aufruf des ChatService mit Kontext und Streaming-Callback
    const finalResponse = await ChatService.sendMessage(text, context, (token) => {
      setStreamedResponse(prev => prev + token);
    });

    setMessages(prev => [...prev, {
      id: (Date.now() + 1).toString(),
      role: 'bot',
      text: finalResponse,
      timestamp: Date.now()
    }]);
    setIsTyping(false);
    setStreamedResponse('');
  };

  return (
    <div className="w-full max-w-3xl mx-auto relative z-20">
      <div className="bg-[#0B1121] border border-indigo-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[600px] relative">
        <div className="bg-[#0F172A] px-6 py-4 border-b border-indigo-500/20 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
                <Scale size={20} />
              </div>
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0F172A]"></div>
            </div>
            <div>
              <div className="text-white font-bold text-sm tracking-wide">{processedTitle}</div>
              <div className="text-indigo-400 text-[10px] uppercase tracking-wider flex items-center gap-1">
                <ShieldCheck size={10} /> {processedSubtitle}
              </div>
            </div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''} animate-fade-in`}>
              <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center mt-1 ${msg.role === 'user' ? 'bg-indigo-100 text-indigo-900' : 'bg-indigo-600 text-white'}`}>
                {msg.role === 'user' ? <User size={14} /> : <Sparkles size={14} />}
              </div>
              <div className={`p-4 rounded-2xl text-sm max-w-[85%] leading-relaxed shadow-sm ${msg.role === 'user'
                ? 'bg-white text-gray-900 rounded-tr-sm'
                : 'bg-indigo-900/40 border border-indigo-500/30 text-indigo-50 rounded-tl-sm'
                }`}>
                {msg.text}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex gap-4 animate-fade-in">
              <div className="w-8 h-8 rounded-full bg-indigo-600 text-white shrink-0 flex items-center justify-center mt-1">
                <RefreshCw size={14} className="animate-spin" />
              </div>
              <div className="p-4 rounded-2xl rounded-tl-sm text-sm max-w-[85%] leading-relaxed shadow-sm bg-indigo-900/40 border border-indigo-500/30 text-indigo-50">
                {streamedResponse || t('chat.processing')}
                <span className="inline-block w-1.5 h-4 bg-indigo-400 ml-1 animate-pulse align-middle"></span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-[#0F172A] border-t border-indigo-500/20 shrink-0">
          {!isTyping && messages.length < 4 && (
            <div className="flex flex-wrap gap-2 mb-4 px-2">
              {processedSuggestions.map((sug, idx) => (
                <button key={idx} onClick={() => handleSend(sug)} className="text-xs bg-indigo-900/30 text-indigo-200 border border-indigo-500/30 px-3 py-1.5 rounded-full hover:bg-indigo-600 hover:text-white hover:border-indigo-500 transition-all duration-300">
                  {sug}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); handleSend(input); }} className="relative flex items-center gap-2 bg-[#0B1121] border border-indigo-500/30 rounded-xl p-1 pl-4">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder || processedTitle}
              className="flex-1 bg-transparent text-white placeholder-indigo-400/50 outline-none py-3 text-sm"
              disabled={isTyping}
            />
            <button type="submit" disabled={isTyping || !input.trim()} className="p-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-50">
              <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
